import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBorderAll, faExchangeAlt, faTag } from '@fortawesome/pro-solid-svg-icons';
import { Nav, Tab } from 'react-bootstrap-5';

import { connect } from 'react-redux';
import { selectShadow, setShadowDirty } from 'bundles/project/actions/index.js';

import FullSizeTab from 'bundles/project/components/full_size_tab';
import LeaseComps from 'bundles/project/components/property_comps/lease';
import SaleComps from 'bundles/project/components/property_comps/sale';
import Shadows from 'components/shadow_grid/shadows';
import { setQueryParameter,  getTabQueryParam } from 'helpers/browser_helpers';

import './styles.scss';

export const tabs = {
  shadows: 'shadows',
  saleComps: 'saleComps',
  leaseComps: 'leaseComps',
};

export function GridsTab({
  coordinates,
  features,
  isGridTabDirty,
  selectedShadow,
  propertyCompsEnabled,
  selectShadow,
  setShadowDirty,
  ...props
}) {
  const [tab, setTab] = useState(getTabQueryParam(tabs, 'gridsTab'));

  function selectTab(activeTab) {
    setQueryParameter('gridsTab', activeTab);
    setTab(activeTab);
  }

  function renderShadows() {
    return (
      <Shadows
        features={features}
        setDirty={setShadowDirty}
        {...props}
        selectedShadow={selectedShadow}
        selectShadow={selectShadow}
      />
    );
  }

  React.useEffect(() => {
    function handleDirtyChange(event) {
      if (event.data.type === 'shadow.dirtyChanged') {
        setShadowDirty(event.data.dirty);
      }
    }
    window.addEventListener('message', handleDirtyChange);
    return () => {
      window.removeEventListener('message', handleDirtyChange);
    };
  }, []);

  let nav;
  const panes = [
    <Tab.Pane eventKey={tabs.shadows} key={tabs.shadows}>
      {renderShadows()}
    </Tab.Pane>
  ];

  if (propertyCompsEnabled && features.shadowGridCoreModels) {
    nav = (
      <Nav activeKey={tab} style={{ zIndex: 0 }} variant="tabs" onSelect={selectTab}>
        <Nav.Item>
          <Nav.Link eventKey={tabs.shadows}>
            <FontAwesomeIcon icon={faBorderAll} size="lg" />
            Grids
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link eventKey={tabs.saleComps}>
            <FontAwesomeIcon icon={faTag} size="lg" />
            Sale Comps
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link eventKey={tabs.leaseComps}>
            <FontAwesomeIcon icon={faExchangeAlt} size="lg" />
            Lease Comps
          </Nav.Link>
        </Nav.Item>
      </Nav>
    );

    panes.push(
      <Tab.Pane eventKey={tabs.saleComps} key={tabs.saleComps}>
        <SaleComps coordinates={coordinates} />
      </Tab.Pane>
    );

    panes.push(
      <Tab.Pane eventKey={tabs.leaseComps} key={tabs.leaseComps}>
        <LeaseComps coordinates={coordinates} />
      </Tab.Pane>
    );
  }

  return (
    <FullSizeTab bodyClassName="has-shadow-grid" className="project-grids-tab">
      <Prompt
        message={() => 'There are unsaved changes - are you sure you want to leave this tab?'}
        when={isGridTabDirty}
      />

      {nav}

      <Tab.Container
        activeKey={tab}
        id="project-grid-tab-subtabs"
        onSelect={() => { /* Tab.Container is weird */ }}
      >
        <Tab.Content animation={false} mountOnEnter unmountOnExit>
          {panes}
        </Tab.Content>
      </Tab.Container>
    </FullSizeTab>
  );
}

GridsTab.propTypes = {
  coordinates: PropTypes.object,
  features: PropTypes.shape({
    shadowGridCoreModels: PropTypes.bool.isRequired
  }),
  isGridTabDirty: PropTypes.bool,
  propertyCompsEnabled: PropTypes.bool,
  selectedShadow: PropTypes.object,
  selectShadow: PropTypes.func.isRequired,
  setShadowDirty: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { latitude, longitude } = state.project.researchProperty;
  let coordinates;
  if (latitude || longitude) {
    coordinates = { latitude, longitude };
  }

  const { canExportShadows, context } = state.shadowGridOptions;

  return {
    canExportShadows,
    context,
    coordinates,
    features: state.features,
    grids: state.project.grids,
    isGridTabDirty: state.shadowGridTab.isDirty,
    propertyCompsEnabled: state.propertyComps && state.propertyComps.enabled,
    selectedShadow: state.shadowGridTab.selectedShadow,
    urls: state.shadowGridOptions.urls,
  };
};

const mapDispatchToProps = {
  selectShadow,
  setShadowDirty,
};

export default connect(mapStateToProps, mapDispatchToProps)(GridsTab);
